import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import styles from "./BarePlayer.module.scss";
function fmt(sec) {
    if (!Number.isFinite(sec) || sec < 0)
        return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
}
/**
 * Голый плеер трека для публичной страницы песни: play/pause + перемотка +
 * тайминг. БЕЗ обложки (она показана отдельно над плеером). Автозапуск на
 * монтировании — если браузер блокирует autoplay без жеста, тихо остаётся на
 * паузе (та же защитная логика, что и в ShareAudioBar, но без обложки).
 */
export const BarePlayer = React.forwardRef(({ src, autoPlay }, handleRef) => {
    const ref = React.useRef(null);
    const barRef = React.useRef(null);
    const [playing, setPlaying] = React.useState(false);
    const [current, setCurrent] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    // Пауза извне (старт видео) — глушим аудио, не трогая позицию.
    React.useImperativeHandle(handleRef, () => ({
        pause: () => {
            ref.current?.pause();
            setPlaying(false);
        },
    }));
    // Автозапуск. Сразу пробуем на монтировании (в webview Telegram/VK и там, где
    // автоплей разрешён, — сыграет). Если браузер заблокировал автоплей со звуком
    // (десктоп без жеста), стартуем на ПЕРВОМ взаимодействии пользователя со
    // страницей. Клики по самому плееру игнорируем — их обрабатывает кнопка play
    // (иначе pointerdown запустил бы трек, а следом click по кнопке поставил бы
    // его на паузу).
    React.useEffect(() => {
        if (!autoPlay)
            return;
        const el = ref.current;
        if (!el)
            return;
        let cleanup = () => { };
        const startOnInteract = (e) => {
            const bar = barRef.current;
            if (bar && e.target instanceof Node && bar.contains(e.target))
                return;
            void el.play();
            cleanup();
        };
        const attachFallback = () => {
            const evs = [
                "pointerdown",
                "keydown",
                "touchstart",
            ];
            evs.forEach((ev) => document.addEventListener(ev, startOnInteract, { passive: true }));
            cleanup = () => evs.forEach((ev) => document.removeEventListener(ev, startOnInteract));
        };
        const p = el.play();
        if (p && typeof p.then === "function") {
            p.then(() => setPlaying(true), attachFallback);
        }
        return () => cleanup();
    }, [autoPlay, src]);
    const toggle = () => {
        const el = ref.current;
        if (!el)
            return;
        if (el.paused) {
            void el.play().then(() => setPlaying(true), () => setPlaying(false));
        }
        else {
            el.pause();
            setPlaying(false);
        }
    };
    const seek = (e) => {
        const el = ref.current;
        if (!el)
            return;
        const t = Number(e.target.value);
        el.currentTime = t;
        setCurrent(t);
    };
    const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
    const safeCurrent = Math.min(Math.max(0, current), safeDuration || 0);
    // Прогресс для заливки трека перемотки (в процентах).
    const progress = safeDuration ? (safeCurrent / safeDuration) * 100 : 0;
    return (_jsxs("div", { className: styles.bar, ref: barRef, children: [_jsx("audio", { ref: ref, src: src, autoPlay: autoPlay, preload: "auto", onTimeUpdate: (e) => setCurrent(e.currentTarget.currentTime), onLoadedMetadata: (e) => {
                    const d = e.currentTarget.duration;
                    setDuration(Number.isFinite(d) ? d : 0);
                }, onPlay: () => setPlaying(true), onPause: () => setPlaying(false), onEnded: () => {
                    setPlaying(false);
                    setCurrent(0);
                } }), _jsx("button", { type: "button", className: styles.playBtn, onClick: toggle, "aria-label": playing ? "Пауза" : "Играть", children: playing ? (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 18 18", fill: "currentColor", "aria-hidden": "true", children: [_jsx("rect", { x: "3", y: "2", width: "4", height: "14", rx: "1" }), _jsx("rect", { x: "11", y: "2", width: "4", height: "14", rx: "1" })] })) : (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 18 18", fill: "currentColor", "aria-hidden": "true", className: styles.playGlyph, children: _jsx("path", { d: "M4 2.5v13l11-6.5z" }) })) }), _jsxs("div", { className: styles.main, children: [_jsx("input", { type: "range", min: 0, max: safeDuration || 1, step: 0.1, value: safeCurrent, onChange: seek, className: styles.seek, style: { "--vp-seek-progress": `${progress}%` }, "aria-label": "\u041F\u0435\u0440\u0435\u043C\u043E\u0442\u043A\u0430", disabled: !safeDuration }), _jsxs("div", { className: styles.timeRow, children: [_jsx("span", { className: styles.time, children: fmt(safeCurrent) }), _jsx("span", { className: styles.time, children: fmt(safeDuration) })] })] })] }));
});
BarePlayer.displayName = "BarePlayer";
export default BarePlayer;
